import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router-dom';
import { formatDate } from '@klp/utils/dateTimeFormatter';
import { Box, Button, Grid, makeStyles } from '@material-ui/core';
import { showToast } from '@klpkonsern/ui';
import {
    Heading,
    Block,
    ActionButton,
    Typography,
    Tooltip,
    ActionLink,
    theme,
    Amount,
    Loader,
    KlpPalette,
} from '@klp/design';

import type { MUIDataTableColumn, MUIDataTableOptions } from 'mui-datatables';
import MUIDataTable from 'mui-datatables';
import type { ContractToRegistration } from '../classes';
import { CheckOffContractRequest } from '../classes/CheckOffContractRequest';
import {
    RegistrationStatuses,
    useGetContractToRegistrationQuery,
    useRegisterContractMutation,
} from '../services/rtkApi';
import {
    ContractManagementRoutes,
    ContractManagementTabs,
} from '@forvalter/routing/Routes';
import { Main } from '@klp/layout/Main';
import { ContractDocument } from '../components/ContractDocument';

const useStyles = makeStyles(() => ({
    D365: {
        maxWidth: '490px',
    },
    dokument: {
        width: '100%',
        borderColor: theme.palette.primary.medium,
        borderRadius: 2,
        border: '1px solid transparent',
    },
    buttons: {
        width: '120px',
        paddingTop: '10px',
        paddingBottom: '10px',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
    },
    placeholder: {
        border: '1px solid transparent',
        borderColor: theme.palette.primary.medium,
        borderRadius: 2,
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        display: 'flex',
        minWidth: '16rem',
        alignItems: 'center',
        '& > *': {
            display: 'flex',
            '&:first-child': {
                flex: '1 1 auto',
            },
            '&:last-child': {
                cursor: 'pointer',
                '&:hover svg': {
                    fill: theme.palette.secondary.main,
                },
            },
        },
    },
    rectangle: {
        color: 'red',
        background: KlpPalette.secondary.light,
        width: '70px',
        marginTop: '30px',
        height: '220px',
    },
    button: {
        textTransform: 'none',
        width: '100px',
    },
    noWrap: {
        whiteSpace: 'nowrap',
    },
    buttonWrapper: {
        width: 'max-content',
        cursor: 'not-allowed',
    },
    previewDownload: {
        cursor: 'pointer',
    },
    warningText: {
        color: 'red',
    },
}));

export default function ShareDocumentView() {
    const { t } = useTranslation();
    const classes = useStyles();
    const history = useHistory();
    const { kontraktId } = useParams<{ kontraktId: string }>();
    const [rows, setRows] = useState<Array<number>>([]);

    const SQUARE_METER_SYMBOL = '\u33A1'; // ㎡

    const {
        data: contractToRegistration,
        isLoading: contractToRegistrationLoading,
        isFetching: contractToRegistrationFetching,
    } = useGetContractToRegistrationQuery({
        kontraktId,
    });

    const [
        registerContract,
        { isLoading: registerContractLoading, isError: registerContractError },
    ] = useRegisterContractMutation();

    const kontraktLinjer = contractToRegistration?.getLinjer() ?? [];
    const noContracts = contractToRegistration?.kontrakter?.length === 0;

    const handleCancel = () => {
        history.push(
            ContractManagementRoutes.tab(ContractManagementTabs.registrering),
        );
    };

    const handleRouting = () => {
        history.push(
            ContractManagementRoutes.tab(ContractManagementTabs.registrering),
        );

        showToast({
            body: t('checkOffContract.successfulSending'),
            type: 'success',
        });
    };

    const handleComplete = () => {
        handleRouting();
    };

    const handleSaveAvsjekk = async (
        avsjekkRows: Array<number>,
        contract?: ContractToRegistration,
    ) => {
        const kontraktLinjeIds: Array<string> = [];
        const currentKontraktLinjeIds = contract?.getLinjer();

        if (currentKontraktLinjeIds) {
            avsjekkRows.forEach((element) => {
                kontraktLinjeIds.push(currentKontraktLinjeIds[element].id);
            });
        }

        if (!contract || !kontraktLinjeIds) {
            return;
        }

        await registerContract(
            new CheckOffContractRequest({
                dokumentId: contract.dokumentId,
                kontraktId: contract.kontrakter?.[0]?.kontraktNummer,
                kontraktlinjeIds: kontraktLinjeIds,
                kommentar: '',
                skalRegistreres: undefined,
                fullfor: true,
            }),
        );
        handleComplete();
    };

    const onRowSelectionChange = (
        _currentRowsSelected: Array<{ dataIndex: number; index: number }>,
        _allRowsSelected: Array<{ dataIndex: number; index: number }>,
        rowsSelected: Array<number>,
    ) => {
        setRows(rowsSelected);
    };

    useEffect(() => {
        const currentKontraktLinjeIds: Array<number> = [];
        contractToRegistration?.getLinjer().forEach((element, index) => {
            if (
                contractToRegistration?.avsjekkKontraktlinjeIds.includes(
                    element.id,
                )
            ) {
                currentKontraktLinjeIds.push(index);
            }
            setRows(currentKontraktLinjeIds);
        });
    }, [
        contractToRegistration,
        contractToRegistrationLoading,
        contractToRegistrationFetching,
    ]);

    if (registerContractError) {
        showToast({ body: t('general.error.message'), type: 'error' });
    }

    const tableColumns: Array<MUIDataTableColumn> = [
        {
            label: t('checkOffContract.table.kontraktId'),
            name: 'kontraktId',
            options: {
                customBodyRenderLite: (dataIndex) => (
                    <Typography klpTypographyVariant="small" klpColor="main">
                        {kontraktLinjer[dataIndex]?.kontraktId}
                    </Typography>
                ),
            },
        },
        {
            label: t('checkOffContract.table.name'),
            name: 'navn',
            options: {
                customBodyRender: (value) => (
                    <Typography klpTypographyVariant="small" klpColor="main">
                        {value}
                    </Typography>
                ),
            },
        },
        {
            label: t('checkOffContract.table.areal'),
            name: 'arealAntall',
            options: {
                filter: false,
                customBodyRenderLite: (dataIndex) => (
                    <Typography klpTypographyVariant="small" klpColor="main">
                        {kontraktLinjer[dataIndex]?.areal > 0 ? (
                            <>
                                <Amount
                                    value={kontraktLinjer[dataIndex]?.areal}
                                />{' '}
                                {SQUARE_METER_SYMBOL}
                            </>
                        ) : kontraktLinjer[dataIndex]?.antall > 0 ? (
                            <>
                                {kontraktLinjer[dataIndex]?.antall}{' '}
                                {t('contract.pcs')}
                            </>
                        ) : (
                            <></>
                        )}
                    </Typography>
                ),
            },
        },
        {
            label: t('checkOffContract.table.kontraktLinjeIds'),
            name: 'kontraktlinjeIds',
            options: {
                display: false,
                viewColumns: false,
            },
        },
        {
            label: t('checkOffContract.table.validFromDate'),
            name: 'gyldigFra',
            options: {
                customBodyRender: (value) => (
                    <Typography klpTypographyVariant="small" klpColor="main">
                        {formatDate(value)}
                    </Typography>
                ),
            },
        },
        {
            label: t('checkOffContract.table.validToDate'),
            name: 'gyldigTil',
            options: {
                customBodyRender: (value) => (
                    <Typography klpTypographyVariant="small" klpColor="main">
                        {formatDate(value)}
                    </Typography>
                ),
            },
        },
        {
            label: t('checkOffContract.table.yearlyPrice'),
            name: 'aarligBelop',
            options: {
                customBodyRender: (value) => (
                    <Typography
                        klpTypographyVariant="small"
                        className={classes.noWrap}
                        klpColor="main"
                    >
                        <Amount value={value} />
                        &#8239; NOK
                    </Typography>
                ),
            },
        },
    ];

    const tableOptions: MUIDataTableOptions = {
        onRowSelectionChange: onRowSelectionChange,
        search: false,
        download: false,
        print: false,
        viewColumns: false,
        filter: false,
        sort: true,
        sortOrder: {
            name: 'gyldigFra',
            direction: 'desc',
        },
        selectableRowsHeader: false,
        rowsSelected: rows,
        filterType: 'multiselect',
        customToolbarSelect: () => <></>,
        textLabels: {
            body: {
                noMatch: t('checkOffContract.table.noRelevantContractLines'),
                toolTip: t('tables.body.toolTip'),
                columnHeaderTooltip: (column) =>
                    t('tables.body.toolTipFor') + column.label,
            },
            pagination: {
                next: t('tables.pagination.next'),
                previous: t('tables.pagination.previous'),
                rowsPerPage: t('tables.pagination.rowsPerPage'),
                displayRows: t('tables.pagination.displayRows'),
            },
            filter: {
                all: t('tables.filter.all'),
                title: t('tables.filter.title'),
                reset: t('tables.filter.reset'),
            },
            toolbar: {
                search: t('tables.toolbar.search'),
                downloadCsv: t('tables.toolbar.downloadCsv'),
                print: t('tables.toolbar.print'),
                viewColumns: t('tables.toolbar.viewColumns'),
                filterTable: t('tables.toolbar.filterTable'),
            },
            viewColumns: {
                title: t('tables.viewColumns.title'),
                titleAria: t('tables.viewColumns.titleAria'),
            },
            selectedRows: {
                text: t('tables.selectedRows.text'),
                delete: t('tables.selectedRows.delete'),
                deleteAria: t('tables.selectedRows.deleteAria'),
            },
        },
        setFilterChipProps: () => {
            return {
                color: 'secondary',
                variant: 'outlined',
            };
        },
    };

    const isLoading =
        contractToRegistrationLoading || contractToRegistrationFetching;
    if (isLoading) {
        return <Loader isLoading={isLoading} />;
    }

    return (
        <Main>
            <Main.Breadcrumbs>
                <Main.Crumb
                    id="contractManagement"
                    label={t('breadcrumbs.contractManagement')}
                    href={ContractManagementRoutes.moduleRoute()}
                />
                <Main.Crumb
                    id="checkOffContract"
                    label={contractToRegistration?.navn}
                />
            </Main.Breadcrumbs>
            <Main.Navigation>
                <ActionLink
                    actionType="linkBack"
                    translation="checkOffContract.backButton"
                    href={ContractManagementRoutes.tab(
                        ContractManagementTabs.registrering,
                    )}
                />
            </Main.Navigation>
            <Grid container={true} direction="column" spacing={3}>
                <Grid
                    container={true}
                    direction="column"
                    justifyContent="flex-start"
                >
                    <Block paddingTop="compact" paddingLeft="compact">
                        <Heading
                            klpHeadingLevel="ONE"
                            data-cy="ShareDocument__Title"
                        >
                            {contractToRegistration?.navn}{' '}
                        </Heading>
                    </Block>
                    <Block paddingTop="compact" paddingLeft="compact">
                        <Heading
                            klpHeadingLevel="THREE"
                            klpColor="dark"
                            translation="checkOffContract.secondaryTitle"
                            translationOptions={{
                                kundeNavn: contractToRegistration?.kundeNavn,
                                kundeNummer:
                                    contractToRegistration?.kundeNummer,
                            }}
                            data-cy="ShareDocument__Secondary__Title"
                        />
                    </Block>
                    <Block
                        paddingTop="compact"
                        paddingLeft="compact"
                        className={classes.warningText}
                    >
                        <Heading
                            klpHeadingLevel="FIVE"
                            klpColor="dark"
                            translation="checkOffContract.shareDocumentWarning"
                        />
                    </Block>
                    <Block paddingTop="compact" paddingLeft="compact">
                        {contractToRegistration?.filMetadata && (
                            <ContractDocument
                                contractDocument={
                                    contractToRegistration.filMetadata
                                }
                            />
                        )}
                    </Block>

                    <Block paddingTop="tiny" paddingLeft="compact">
                        <MUIDataTable
                            title=""
                            data={kontraktLinjer}
                            columns={tableColumns}
                            options={tableOptions}
                        />
                    </Block>
                </Grid>
                <Block paddingLeft="compact" paddingTop="compact">
                    <Grid
                        container={true}
                        direction="row"
                        alignItems="center"
                        justifyContent="flex-start"
                        spacing={3}
                    >
                        <Grid item={true}>
                            <Block>
                                <ActionButton
                                    actionType="cancel"
                                    translation="checkOffContract.cancelButton"
                                    onClick={handleCancel}
                                />
                            </Block>
                        </Grid>
                        <Grid item={true}>
                            <Block>
                                {noContracts ? (
                                    <Tooltip
                                        translation="checkOffContract.tooltipSendButton"
                                        title=""
                                    >
                                        <Box className={classes.buttonWrapper}>
                                            <Button
                                                disabled={
                                                    noContracts ||
                                                    registerContractLoading
                                                }
                                                variant="contained"
                                                color="secondary"
                                                onClick={() =>
                                                    handleSaveAvsjekk(
                                                        rows,
                                                        contractToRegistration,
                                                    )
                                                }
                                            >
                                                {' '}
                                                {t(
                                                    contractToRegistration.registreringStatus ===
                                                        RegistrationStatuses.FULLFORT
                                                        ? 'checkOffContract.shareButtonCompleted'
                                                        : 'checkOffContract.shareButton',
                                                )}
                                            </Button>
                                        </Box>
                                    </Tooltip>
                                ) : (
                                    <Button
                                        disabled={
                                            noContracts ||
                                            registerContractLoading
                                        }
                                        variant="contained"
                                        color="secondary"
                                        onClick={() =>
                                            handleSaveAvsjekk(
                                                rows,
                                                contractToRegistration,
                                            )
                                        }
                                    >
                                        {' '}
                                        {t(
                                            RegistrationStatuses.FULLFORT
                                                ? 'checkOffContract.shareButtonCompleted'
                                                : 'checkOffContract.shareButton',
                                        )}
                                    </Button>
                                )}
                            </Block>
                        </Grid>
                    </Grid>
                </Block>
            </Grid>
        </Main>
    );
}
